import { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { browseSourceShare } from "@/api/filth-source-control-api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import {
  sourceControlKey as key,
  pathUnderRoots,
  sourceControlError,
} from "./filth-source-control-utils"

export function FilthShareBrowser({
  backendId,
  roots,
  value,
  onChange,
}: {
  backendId: string
  roots: string[]
  value: string
  onChange: (v: string) => void
}) {
  const { t } = useTranslation()
  const [browse, setBrowse] = useState<string | null>(null)
  const directories = useQuery({
    queryKey: ["filth-share", backendId, browse],
    queryFn: () => browseSourceShare(backendId, browse!),
    enabled: browse !== null,
    retry: false,
  })
  return (
    <div className="space-y-4">
      <Label className="grid gap-2">
        {t(`${key}.allowedRoots`)}
        <select
          aria-label={t(`${key}.allowedRoots`)}
          className="bg-background rounded-md border p-2 text-sm"
          value={roots.includes(value) ? value : ""}
          onChange={(e) => {
            onChange(e.target.value)
            setBrowse(e.target.value || null)
          }}
        >
          <option value="">{t(`${key}.choose`)}</option>
          {roots.map((root) => (
            <option key={root} value={root}>
              {root}
            </option>
          ))}
        </select>
      </Label>
      <Label className="grid gap-2">
        {t(`${key}.path`)}
        <Input value={value} onChange={(e) => onChange(e.target.value)} />
      </Label>
      <Button
        disabled={!pathUnderRoots(value, roots) || directories.isFetching}
        type="button"
        variant="outline"
        onClick={() => {
          setBrowse(value)
          if (browse === value) void directories.refetch()
        }}
      >
        {t(`${key}.browse`)}
      </Button>
      {directories.error && (
        <p role="alert">{t(`${key}.errors.${sourceControlError(directories.error)}`)}</p>
      )}
      <div className="flex flex-wrap gap-2">
        {directories.data?.directories.map((v) => (
          <Button
            key={v.path}
            type="button"
            variant="outline"
            onClick={() => {
              onChange(v.path)
              setBrowse(v.path)
            }}
          >
            {v.name}
          </Button>
        ))}
      </div>
    </div>
  )
}
